import { z } from "zod";

import {
  boundedTextSchema,
  localOggAudioReferenceSchema,
  nonNegativeSafeIntegerSchema
} from "@/domain/schemas";

export const MAX_RHYTHM_TOKENS = 12;

const rhythmTokenSchema = boundedTextSchema(40);
const rhythmTokenAudioSchema = z.record(
  rhythmTokenSchema,
  localOggAudioReferenceSchema
);

export function normalizeRhythmToken(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLocaleLowerCase("en-US");
}

export const rhythmPayloadSchema = z
  .strictObject({
    kind: z.literal("rhythm-phrases"),
    modelAudio: localOggAudioReferenceSchema,
    tokenAudio: rhythmTokenAudioSchema,
    caption: boundedTextSchema(300),
    beatCuesMs: z
      .array(nonNegativeSafeIntegerSchema)
      .min(1)
      .max(MAX_RHYTHM_TOKENS),
    toleranceMs: z.number().int().positive().max(5_000),
    answerTokens: z
      .array(rhythmTokenSchema)
      .min(1)
      .max(MAX_RHYTHM_TOKENS)
  })
  .superRefine((payload, context) => {
    if (payload.beatCuesMs.length !== payload.answerTokens.length) {
      context.addIssue({
        code: "custom",
        path: ["beatCuesMs"],
        message: "Beat cue count must match answer token count"
      });
    }

    const expectedTokenKeys = new Set(payload.answerTokens);
    const actualTokenKeys = Object.keys(payload.tokenAudio);
    if (
      actualTokenKeys.length !== expectedTokenKeys.size ||
      actualTokenKeys.some((token) => !expectedTokenKeys.has(token))
    ) {
      context.addIssue({
        code: "custom",
        path: ["tokenAudio"],
        message: "Token audio keys must exactly match answer tokens"
      });
    }

    payload.beatCuesMs.forEach((cue, index) => {
      if (index > 0 && cue <= (payload.beatCuesMs[index - 1] ?? cue)) {
        context.addIssue({
          code: "custom",
          path: ["beatCuesMs", index],
          message: "Beat cues must be strictly increasing"
        });
      }
    });

    const expectedCaption = payload.answerTokens
      .map(normalizeRhythmToken)
      .join(" ");
    if (normalizeRhythmToken(payload.caption) !== expectedCaption) {
      context.addIssue({
        code: "custom",
        path: ["caption"],
        message: "Caption must match the ordered answer tokens"
      });
    }
  });

export type RhythmPayload = z.infer<typeof rhythmPayloadSchema>;
